import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useService } from 'react-service-locator';
import { zodResolver } from '@hookform/resolvers/zod';
import { up } from 'styled-breakpoints';
import styled, { css } from 'styled-components';
import { match } from 'ts-pattern';
import * as zod from 'zod';
import { BaseMetricPeriodWhereInput } from '@lgg/isomorphic/base-types';
import {
  AvailableComparePeriodsForPeriod,
  getPeriodDateRange,
} from '@lgg/isomorphic/dashboard-date-helpers';
import { Dayjs } from '@lgg/isomorphic/dayjs';
import { getDatesDifferenceInUnits } from '@lgg/isomorphic/i18n/date-utils';
import { whenDefined } from '@lgg/isomorphic/when-defined';
import {
  BaseDateOption,
  ComparedToDateOption,
  PeriodFilterContext,
} from 'src/components/domain/metrics/period-filter-context';
import {
  LggDropdownButtonWithoutOverlay,
  LggSelectableOptionsDropdownButton,
} from 'src/components/general/button/dropdown-button';
import { LggButton } from 'src/components/general/button/lgg-button';
import { BottomDrawer } from 'src/components/general/drawer/bottom/bottom-drawer';
import {
  DrawerSelectableOption,
  SelectableOptionsDrawer,
} from 'src/components/general/drawer/bottom/selectable-options-bottom-drawer';
import { LggRangePicker } from 'src/components/general/inputs/custom-date-pickers/lgg-range-picker';
import { GenericInput } from 'src/components/general/inputs/generic-input';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useDateHelpers } from 'src/hooks/use-date-helpers';
import { useFormatDate } from 'src/hooks/use-format-date';
import { useVisible } from 'src/hooks/use-visible';
import { AuthorizationService } from 'src/services/authorization.service';

const baseDropdownStyles = css`
  &,
  &:hover {
    box-shadow: none;
  }

  .arrow-down-icon {
    margin-left: 5px;
  }

  .dropdown-label {
    &,
    &:active,
    &:hover {
      color: ${({ theme }) => theme.colors.gogo};
    }
  }
`;

const FilterDateDropdownButton = styled(LggSelectableOptionsDropdownButton)`
  ${baseDropdownStyles};
`;

const StyledDropdownButton = styled(LggDropdownButtonWithoutOverlay)`
  ${baseDropdownStyles}

  border: 1px solid ${({ theme }) => theme.colors.koala};
  font-size: 13px;
  height: 32px;
  padding: 8px 10px;

  ${up('md')} {
    border: none;
    font-size: 14px;
    height: 38px;
    padding: 10px 15px;
  }
`;

const LeftContainer = styled(FlexRow)`
  align-items: center;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
`;

const FilterDateRangePreview = styled(FlexRow)`
  align-items: center;
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 14px;
  height: 38px;
  line-height: 18px;
  padding-left: 5px;
  white-space: nowrap;
`;

const ComparedToDropdownLabel = styled(FlexRow)`
  align-items: center;
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 13px;
  font-style: normal;
  letter-spacing: -0.78px;
  line-height: 16px;
  padding: 0 5px;
  white-space: nowrap;

  ${up('md')} {
    padding: 0 15px;
  }
`;

const MobileOptionsContainer = styled(FlexColumn)`
  padding: 20px;
  gap: 17px;

  & > :last-child {
    margin-bottom: 63px;
  }
`;

const MobilePresetFilterLabel = styled(FlexRow)`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 13px;
  line-height: 16px;
  white-space: break-spaces;

  strong {
    font-family: ${({ theme }) => theme.font.medium};
    line-height: 18px;
  }
`;

const dateRangeSchema = zod.object({
  fromDate: zod.date(),
  toDate: zod.date(),
});

const mobileFormSchema = zod.object({
  basePeriod: zod.nativeEnum(BaseDateOption),
  comparePeriod: zod.nativeEnum(ComparedToDateOption),
  basePeriodRange: dateRangeSchema,
  comparePeriodRange: dateRangeSchema,
});

const FooterContainer = styled(FlexRow)`
  gap: 10px;
  border-top: 1px solid ${({ theme }) => theme.colors.koala};
  padding: 15px 20px;
`;

type MobileFormValues = zod.infer<typeof mobileFormSchema>;

export const PeriodFilter = () => {
  const breakpointUpMd = useBreakpoint(up('md'));
  const baseDateOptionsVisibilityHandler = useVisible();
  const comparedToDateOptionsVisibilityHandler = useVisible();
  const authState = useService(AuthorizationService);
  const timezone = authState.getTimezone();
  const basePeriodCustomPickerVisibilityHandler = useVisible();
  const comparePeriodCustomPickerVisibilityHandler = useVisible();
  const mobileOptionsVisibilityHandler = useVisible();

  const {
    setBaseDateFilter,
    baseDateFilter,
    baseDateFilterDates,
    setComparedToDateFilter,
    comparedToDateFilter,
    setBaseDateFilterDates,
    availableComparePeriods,
    setComparedToDateFilterDates,
    comparedToDateFilterDates,
    getSelectedComparePeriodInfo,
    resetDateFilters,
    baseDateFilterDefaultValue,
  } = useContext(PeriodFilterContext);

  const availableCompareDefaultValue = useMemo(
    () =>
      getSelectedComparePeriodInfo({
        currentComparePeriod: comparedToDateFilter,
        fromDate: baseDateFilterDates.fromDate,
        toDate: baseDateFilterDates.toDate,
      }).availableComparePeriods,
    [
      baseDateFilterDates.fromDate,
      baseDateFilterDates.toDate,
      comparedToDateFilter,
      getSelectedComparePeriodInfo,
    ],
  );

  const [mobileCompareOptions, setMobileCompareOptions] =
    useState<AvailableComparePeriodsForPeriod>(availableCompareDefaultValue);

  const mobileForm = useForm<MobileFormValues>({
    resolver: zodResolver(mobileFormSchema),
    defaultValues: {
      basePeriod: baseDateFilter,
      comparePeriod: comparedToDateFilter,
      basePeriodRange: {
        ...baseDateFilterDates,
      },
      comparePeriodRange: {
        ...comparedToDateFilterDates,
      },
    },
  });

  const watchedMobileComparePeriod = mobileForm.watch('comparePeriod');
  const watchedMobileBasePeriod = mobileForm.watch('basePeriod');
  const watchedMobileBasePeriodRange = mobileForm.watch('basePeriodRange');

  useEffect(() => {
    const { availableComparePeriods, selectedCompareRange } =
      getSelectedComparePeriodInfo({
        fromDate: watchedMobileBasePeriodRange.fromDate,
        toDate: watchedMobileBasePeriodRange.toDate,
        currentComparePeriod: watchedMobileComparePeriod,
      });

    if (!selectedCompareRange) {
      const previousPeriodRange = availableComparePeriods.previousPeriod;

      mobileForm.setValue('comparePeriod', ComparedToDateOption.PREVIOUS_PERIOD);
      mobileForm.setValue('comparePeriodRange', {
        fromDate: previousPeriodRange.fromDate.toDate(),
        toDate: previousPeriodRange.toDate.toDate(),
      });
    } else {
      mobileForm.setValue('comparePeriodRange', {
        fromDate: selectedCompareRange.fromDate.toDate(),
        toDate: selectedCompareRange.toDate.toDate(),
      });
    }

    setMobileCompareOptions(availableComparePeriods);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedMobileBasePeriod,
    watchedMobileBasePeriodRange.fromDate,
    watchedMobileBasePeriodRange.toDate,
  ]);

  const { formatDateAndTimePickerDate } = useFormatDate();
  const { t } = useTranslation(['metrics', 'common']);

  const baseDateOptionLabelMap = useMemo(() => {
    return {
      [BaseDateOption.TODAY]: t('metrics:periodFilter.basePeriod.options.today'),
      [BaseDateOption.YESTERDAY]: t('metrics:periodFilter.basePeriod.options.yesterday'),
      [BaseDateOption.LAST_7_DAYS]: t(
        'metrics:periodFilter.basePeriod.options.last7Days',
      ),
      [BaseDateOption.LAST_4_WEEKS]: t(
        'metrics:periodFilter.basePeriod.options.last4Weeks',
      ),
      [BaseDateOption.LAST_3_MONTHS]: t(
        'metrics:periodFilter.basePeriod.options.last3Months',
      ),
      [BaseDateOption.LAST_12_MONTHS]: t(
        'metrics:periodFilter.basePeriod.options.last12Months',
      ),
      [BaseDateOption.MONTH_TO_DATE]: t(
        'metrics:periodFilter.basePeriod.options.monthToDate',
      ),
      [BaseDateOption.QUARTER_TO_DATE]: t(
        'metrics:periodFilter.basePeriod.options.quarterToDate',
      ),
      [BaseDateOption.YEAR_TO_DATE]: t(
        'metrics:periodFilter.basePeriod.options.yearToDate',
      ),
      [BaseDateOption.CUSTOM]: t('common:custom'),
    };
  }, [t]);

  const comparedToOptionLabelMap = useMemo(() => {
    return {
      [ComparedToDateOption.PREVIOUS_PERIOD]: t(
        'metrics:periodFilter.comparisonPeriod.options.previousPeriod',
      ),
      [ComparedToDateOption.PREVIOUS_MONTH]: t(
        'metrics:periodFilter.comparisonPeriod.options.previousMonth',
      ),
      [ComparedToDateOption.PREVIOUS_QUARTER]: t(
        'metrics:periodFilter.comparisonPeriod.options.previousQuarter',
      ),
      [ComparedToDateOption.PREVIOUS_YEAR]: t(
        'metrics:periodFilter.comparisonPeriod.options.previousYear',
      ),
      [ComparedToDateOption.CUSTOM]: t('common:custom'),
    };
  }, [t]);

  const comparedToOptionShortLabelMap = useMemo(() => {
    return {
      [ComparedToDateOption.PREVIOUS_PERIOD]: t(
        'metrics:periodFilter.comparisonPeriod.options.previousPeriod_short',
      ),
      [ComparedToDateOption.PREVIOUS_MONTH]: t(
        'metrics:periodFilter.comparisonPeriod.options.previousMonth_short',
      ),
      [ComparedToDateOption.PREVIOUS_QUARTER]: t(
        'metrics:periodFilter.comparisonPeriod.options.previousQuarter_short',
      ),
      [ComparedToDateOption.PREVIOUS_YEAR]: t(
        'metrics:periodFilter.comparisonPeriod.options.previousYear_short',
      ),
      [ComparedToDateOption.CUSTOM]: t('common:custom'),
    };
  }, [t]);

  const baseDateOptions = useMemo(() => {
    const getBaseOption = (value: BaseDateOption) => ({
      label: baseDateOptionLabelMap[value],
      value: value,
      'data-lgg-id': `base-date-filter-${value}`,
    });

    const getOption = (value: BaseDateOption) => {
      // eslint-disable-next-line custom-rules/require-try-catch-for-exhaustive
      const optionPeriod: BaseMetricPeriodWhereInput = match(value)
        .with(BaseDateOption.TODAY, () => ({
          _isToday: true,
        }))
        .with(BaseDateOption.YESTERDAY, () => ({
          _isYesterday: true,
        }))
        .with(BaseDateOption.LAST_7_DAYS, () => ({
          _isLastXDays: 7,
        }))
        .with(BaseDateOption.LAST_4_WEEKS, () => ({
          _isLastXWeeks: 4,
        }))
        .with(BaseDateOption.LAST_3_MONTHS, () => ({
          _isLastXMonths: 3,
        }))
        .with(BaseDateOption.LAST_12_MONTHS, () => ({
          _isLastXMonths: 12,
        }))
        .with(BaseDateOption.MONTH_TO_DATE, () => ({
          _isThisMonth: true,
        }))
        .with(BaseDateOption.QUARTER_TO_DATE, () => ({
          _isThisQuarter: true,
        }))
        .with(BaseDateOption.YEAR_TO_DATE, () => ({
          _isThisYear: true,
        }))
        .with(BaseDateOption.CUSTOM, () => ({
          _isBetween: {
            from: baseDateFilterDates.fromDate,
            to: baseDateFilterDates.toDate,
          },
        }))
        .exhaustive();

      return {
        ...getBaseOption(value),
        onClick: () => {
          const dateRange = getPeriodDateRange({
            timezone,
            period: optionPeriod,
          });

          if (breakpointUpMd) {
            setBaseDateFilter(value);

            if (dateRange) {
              setBaseDateFilterDates(dateRange);
            }
          } else {
            mobileForm.setValue('basePeriod', value);

            if (dateRange) {
              mobileForm.setValue('basePeriodRange', dateRange);
            }
          }
        },
      };
    };

    return [
      getOption(BaseDateOption.TODAY),
      getOption(BaseDateOption.YESTERDAY),
      getOption(BaseDateOption.LAST_7_DAYS),
      getOption(BaseDateOption.LAST_4_WEEKS),
      getOption(BaseDateOption.LAST_3_MONTHS),
      getOption(BaseDateOption.LAST_12_MONTHS),
      getOption(BaseDateOption.MONTH_TO_DATE),
      getOption(BaseDateOption.QUARTER_TO_DATE),
      getOption(BaseDateOption.YEAR_TO_DATE),
      {
        ...getBaseOption(BaseDateOption.CUSTOM),
        onClick: () => {
          basePeriodCustomPickerVisibilityHandler.show();
        },
      },
    ];
  }, [
    baseDateFilterDates.fromDate,
    baseDateFilterDates.toDate,
    baseDateOptionLabelMap,
    basePeriodCustomPickerVisibilityHandler,
    breakpointUpMd,
    mobileForm,
    setBaseDateFilter,
    setBaseDateFilterDates,
    timezone,
  ]);

  const getDateRangePreviewLabel = useCallback(
    (from: Date, to: Date) =>
      `(${formatDateAndTimePickerDate(from)} - ${formatDateAndTimePickerDate(to)})`,
    [formatDateAndTimePickerDate],
  );

  const comparedToDateOptions: DrawerSelectableOption[] = useMemo(() => {
    const { previousMonth, previousPeriod, previousQuarter, previousYear } =
      breakpointUpMd ? availableComparePeriods : mobileCompareOptions;

    const getOption = (value: ComparedToDateOption, fromDate: Dayjs, toDate: Dayjs) => ({
      label: `${comparedToOptionLabelMap[value]} ${getDateRangePreviewLabel(
        fromDate.toDate(),
        toDate.toDate(),
      )}`,
      value: value,
      onClick: () => {
        if (breakpointUpMd) {
          setComparedToDateFilter(value);
          setComparedToDateFilterDates({
            fromDate: fromDate.toDate(),
            toDate: toDate.toDate(),
          });
        } else {
          mobileForm.setValue('comparePeriod', value);
          mobileForm.setValue('comparePeriodRange', {
            fromDate: fromDate.toDate(),
            toDate: toDate.toDate(),
          });
        }
      },
    });

    const presetOptions: DrawerSelectableOption[] = [];

    whenDefined(previousPeriod, (previousPeriod) =>
      presetOptions.push(
        getOption(
          ComparedToDateOption.PREVIOUS_PERIOD,
          previousPeriod.fromDate,
          previousPeriod.toDate,
        ),
      ),
    );

    whenDefined(previousMonth, (previousMonth) =>
      presetOptions.push(
        getOption(
          ComparedToDateOption.PREVIOUS_MONTH,
          previousMonth.fromDate,
          previousMonth.toDate,
        ),
      ),
    );

    whenDefined(previousQuarter, (previousQuarter) =>
      presetOptions.push(
        getOption(
          ComparedToDateOption.PREVIOUS_QUARTER,
          previousQuarter.fromDate,
          previousQuarter.toDate,
        ),
      ),
    );

    whenDefined(previousYear, (previousYear) =>
      presetOptions.push(
        getOption(
          ComparedToDateOption.PREVIOUS_YEAR,
          previousYear.fromDate,
          previousYear.toDate,
        ),
      ),
    );

    return [
      ...presetOptions,
      {
        label: comparedToOptionLabelMap[ComparedToDateOption.CUSTOM],
        value: ComparedToDateOption.CUSTOM,
        onClick: comparePeriodCustomPickerVisibilityHandler.show,
      },
    ];
  }, [
    availableComparePeriods,
    breakpointUpMd,
    comparePeriodCustomPickerVisibilityHandler.show,
    comparedToOptionLabelMap,
    getDateRangePreviewLabel,
    mobileCompareOptions,
    mobileForm,
    setComparedToDateFilter,
    setComparedToDateFilterDates,
  ]);

  const formatDatePreview = useCallback(
    (startDate: Date, endDate: Date) =>
      `${formatDateAndTimePickerDate(startDate)} - ${formatDateAndTimePickerDate(
        endDate,
      )}`,
    [formatDateAndTimePickerDate],
  );

  const baseDateRangePreview = useMemo(
    () => (
      <FilterDateRangePreview data-lgg-id="date-filter-range-preview">
        {formatDatePreview(baseDateFilterDates.fromDate, baseDateFilterDates.toDate)}
      </FilterDateRangePreview>
    ),
    [baseDateFilterDates.fromDate, baseDateFilterDates.toDate, formatDatePreview],
  );

  const compareDateRangePreview = useMemo(
    () => (
      <FilterDateRangePreview data-lgg-id="date-filter-compare-range-preview">
        {formatDatePreview(
          comparedToDateFilterDates.fromDate,
          comparedToDateFilterDates.toDate,
        )}
      </FilterDateRangePreview>
    ),
    [
      comparedToDateFilterDates.fromDate,
      comparedToDateFilterDates.toDate,
      formatDatePreview,
    ],
  );

  const presetDropdown = useMemo(() => {
    return breakpointUpMd ? (
      <FilterDateDropdownButton
        options={baseDateOptions}
        selectedValue={baseDateFilter}
        size="regular"
        variant="defaultWhite"
        visibilityHandler={baseDateOptionsVisibilityHandler}
        data-lgg-id="date-filter-dropdown-trigger"
        customDropdownProps={{
          placement: 'bottomRight',
          overlayStyle: {
            minWidth: 'unset',
            width: '170px',
          },
        }}
      >
        <span>{baseDateOptionLabelMap[baseDateFilter]}:</span>
        {baseDateRangePreview}
      </FilterDateDropdownButton>
    ) : (
      <>
        <GenericInput
          active={baseDateOptionsVisibilityHandler.visible}
          onClick={baseDateOptionsVisibilityHandler.show}
          label={t('metrics:periodFilter.timePeriod')}
          value={
            <MobilePresetFilterLabel>
              {`${baseDateOptionLabelMap[mobileForm.getValues().basePeriod]}: `}
              <strong>
                {formatDatePreview(
                  mobileForm.getValues().basePeriodRange.fromDate,
                  mobileForm.getValues().basePeriodRange.toDate,
                )}
              </strong>
            </MobilePresetFilterLabel>
          }
          showCaret
        />
        <SelectableOptionsDrawer
          onClose={baseDateOptionsVisibilityHandler.close}
          visible={baseDateOptionsVisibilityHandler.visible}
          options={baseDateOptions}
          selectedValue={mobileForm.getValues().basePeriod}
          title={t('metrics:periodFilter.basePeriod.selectPeriod')}
        />
      </>
    );
  }, [
    breakpointUpMd,
    baseDateOptions,
    baseDateFilter,
    baseDateOptionsVisibilityHandler,
    baseDateOptionLabelMap,
    baseDateRangePreview,
    t,
    mobileForm,
    formatDatePreview,
  ]);

  const comparedToPresetDropdown = useMemo(() => {
    return breakpointUpMd ? (
      <FilterDateDropdownButton
        options={comparedToDateOptions}
        selectedValue={comparedToDateFilter}
        size="regular"
        variant="defaultWhite"
        visibilityHandler={comparedToDateOptionsVisibilityHandler}
        data-lgg-id="compared-to-date-filter-dropdown-trigger"
      >
        <span>{comparedToOptionShortLabelMap[comparedToDateFilter]}:</span>
        {compareDateRangePreview}
      </FilterDateDropdownButton>
    ) : (
      <>
        <GenericInput
          active={comparedToDateOptionsVisibilityHandler.visible}
          onClick={comparedToDateOptionsVisibilityHandler.show}
          label={t('metrics:periodFilter.comparisonPeriod.comparedTo')}
          value={
            <MobilePresetFilterLabel>
              {`${comparedToOptionLabelMap[mobileForm.getValues().comparePeriod]}: `}
              <strong>
                {formatDatePreview(
                  mobileForm.getValues().comparePeriodRange.fromDate,
                  mobileForm.getValues().comparePeriodRange.toDate,
                )}
              </strong>
            </MobilePresetFilterLabel>
          }
          showCaret
        />
        <SelectableOptionsDrawer
          onClose={comparedToDateOptionsVisibilityHandler.close}
          visible={comparedToDateOptionsVisibilityHandler.visible}
          options={comparedToDateOptions}
          selectedValue={mobileForm.getValues().comparePeriod}
          title={t('metrics:periodFilter.comparisonPeriod.comparedTo')}
        />
      </>
    );
  }, [
    breakpointUpMd,
    comparedToDateOptions,
    comparedToDateFilter,
    comparedToDateOptionsVisibilityHandler,
    comparedToOptionShortLabelMap,
    compareDateRangePreview,
    t,
    comparedToOptionLabelMap,
    mobileForm,
    formatDatePreview,
  ]);

  const { isAfter, isBefore, isSameDay, subDays, isFuture, initDayJsDate } =
    useDateHelpers();

  const { days: daysCountInRange } = getDatesDifferenceInUnits(
    baseDateFilterDates.fromDate,
    baseDateFilterDates.toDate,
  );

  const mobileComparePeriodInput = useMemo(() => {
    const basePeriodRange = mobileForm.getValues().basePeriodRange;
    const { days: daysCountInRange } = getDatesDifferenceInUnits(
      basePeriodRange.fromDate,
      basePeriodRange.toDate,
    );

    return (
      <Controller
        control={mobileForm.control}
        name="comparePeriod"
        render={({ field }) => {
          return (
            <LggRangePicker
              visibilityHandler={comparePeriodCustomPickerVisibilityHandler}
              onApply={(startDate, endDate) => {
                mobileForm.setValue('comparePeriodRange', {
                  fromDate: startDate.toDate(),
                  toDate: endDate.toDate(),
                });
                mobileForm.setValue('comparePeriod', ComparedToDateOption.CUSTOM);
              }}
              startDateInit={
                field.value === ComparedToDateOption.CUSTOM
                  ? mobileForm.getValues().comparePeriodRange.fromDate
                  : null
              }
              endDateInit={
                field.value === ComparedToDateOption.CUSTOM
                  ? mobileForm.getValues().comparePeriodRange.toDate
                  : null
              }
              disableDateOverride={(dateToRender) => {
                const currentDateDateObject = dateToRender.toDate();
                const { fromDate, toDate } = basePeriodRange;

                const isWithinBaseRange =
                  isAfter(currentDateDateObject, subDays(fromDate, daysCountInRange)) &&
                  isBefore(currentDateDateObject, toDate);
                const isAfterMaxAllowedDate =
                  isAfter(
                    currentDateDateObject,
                    subDays(initDayJsDate(new Date()).toDate(), daysCountInRange),
                  ) || isAfter(currentDateDateObject, toDate);
                const isBoundaryDate =
                  isSameDay(currentDateDateObject, fromDate) ||
                  isSameDay(currentDateDateObject, toDate);
                const isFutureDate = isFuture(currentDateDateObject);

                return (
                  isWithinBaseRange ||
                  isAfterMaxAllowedDate ||
                  isBoundaryDate ||
                  isFutureDate
                );
              }}
              onSelectOverride={({
                setActivePicker,
                setStartDate,
                setEndDate,
                selectedDate,
              }) => {
                setStartDate(selectedDate);
                setEndDate(selectedDate.add(daysCountInRange, 'day').endOf('day'));
                setActivePicker('START');
              }}
              disableEndDate
            >
              {comparedToPresetDropdown}
            </LggRangePicker>
          );
        }}
      />
    );
  }, [
    comparePeriodCustomPickerVisibilityHandler,
    comparedToPresetDropdown,
    initDayJsDate,
    isAfter,
    isBefore,
    isFuture,
    isSameDay,
    mobileForm,
    subDays,
  ]);

  return breakpointUpMd ? (
    <>
      <LeftContainer>
        <LggRangePicker
          visibilityHandler={basePeriodCustomPickerVisibilityHandler}
          onApply={(startDate, endDate) => {
            setBaseDateFilterDates({
              fromDate: startDate.toDate(),
              toDate: endDate.toDate(),
            });
            setBaseDateFilter(BaseDateOption.CUSTOM);
          }}
          startDateInit={
            baseDateFilter === BaseDateOption.CUSTOM ? baseDateFilterDates.fromDate : null
          }
          endDateInit={
            baseDateFilter === BaseDateOption.CUSTOM ? baseDateFilterDates.toDate : null
          }
        >
          {presetDropdown}
        </LggRangePicker>
      </LeftContainer>
      <ComparedToDropdownLabel>
        {t('metrics:periodFilter.comparisonPeriod.comparedTo')}
      </ComparedToDropdownLabel>
      <LggRangePicker
        visibilityHandler={comparePeriodCustomPickerVisibilityHandler}
        onApply={(startDate, endDate) => {
          setComparedToDateFilterDates({
            fromDate: startDate.toDate(),
            toDate: endDate.toDate(),
          });
          setComparedToDateFilter(ComparedToDateOption.CUSTOM);
        }}
        startDateInit={
          comparedToDateFilter === ComparedToDateOption.CUSTOM
            ? comparedToDateFilterDates.fromDate
            : null
        }
        endDateInit={
          comparedToDateFilter === ComparedToDateOption.CUSTOM
            ? comparedToDateFilterDates.toDate
            : null
        }
        disableDateOverride={(dateToRender) => {
          const currentDateDateObject = dateToRender.toDate();
          const { fromDate, toDate } = baseDateFilterDates;

          const isWithinBaseRange =
            isAfter(currentDateDateObject, subDays(fromDate, daysCountInRange)) &&
            isBefore(currentDateDateObject, toDate);
          const isAfterMaxAllowedDate =
            isAfter(
              currentDateDateObject,
              subDays(initDayJsDate(new Date()).toDate(), daysCountInRange),
            ) || isAfter(currentDateDateObject, toDate);
          const isBoundaryDate =
            isSameDay(currentDateDateObject, fromDate) ||
            isSameDay(currentDateDateObject, toDate);
          const isFutureDate = isFuture(currentDateDateObject);

          return (
            isWithinBaseRange || isAfterMaxAllowedDate || isBoundaryDate || isFutureDate
          );
        }}
        onSelectOverride={({
          setActivePicker,
          setStartDate,
          setEndDate,
          selectedDate,
        }) => {
          setStartDate(selectedDate);
          setEndDate(selectedDate.add(daysCountInRange, 'day').endOf('day'));
          setActivePicker('START');
        }}
        disableEndDate
      >
        {comparedToPresetDropdown}
      </LggRangePicker>
    </>
  ) : (
    <>
      <StyledDropdownButton
        isActive={
          baseDateFilter !== BaseDateOption.TODAY ||
          comparedToDateFilter !== ComparedToDateOption.PREVIOUS_PERIOD
        }
        onClick={mobileOptionsVisibilityHandler.show}
        size="regular"
        variant="defaultWhite"
      >
        {baseDateOptionLabelMap[baseDateFilter]}
      </StyledDropdownButton>
      <BottomDrawer
        onClose={mobileOptionsVisibilityHandler.close}
        visible={mobileOptionsVisibilityHandler.visible}
        title={t('metrics:periodFilter.timePeriod')}
      >
        <MobileOptionsContainer>
          <Controller
            render={({ field }) => {
              return (
                <LggRangePicker
                  visibilityHandler={basePeriodCustomPickerVisibilityHandler}
                  onApply={(startDate, endDate) => {
                    mobileForm.setValue('basePeriodRange', {
                      fromDate: startDate.toDate(),
                      toDate: endDate.toDate(),
                    });
                    mobileForm.setValue('basePeriod', BaseDateOption.CUSTOM);
                  }}
                  startDateInit={
                    field.value === BaseDateOption.CUSTOM
                      ? mobileForm.getValues().basePeriodRange.fromDate
                      : null
                  }
                  endDateInit={
                    field.value === BaseDateOption.CUSTOM
                      ? mobileForm.getValues().basePeriodRange.toDate
                      : null
                  }
                >
                  {presetDropdown}
                </LggRangePicker>
              );
            }}
            control={mobileForm.control}
            name="basePeriod"
          />
          {mobileComparePeriodInput}
        </MobileOptionsContainer>
        <FooterContainer>
          <LggButton
            onClick={() => {
              resetDateFilters();

              mobileForm.reset({
                basePeriod: BaseDateOption.TODAY,
                comparePeriod: ComparedToDateOption.PREVIOUS_PERIOD,
                basePeriodRange: baseDateFilterDefaultValue,
                comparePeriodRange: {
                  fromDate: availableCompareDefaultValue.previousPeriod.fromDate.toDate(),
                  toDate: availableCompareDefaultValue.previousPeriod.toDate.toDate(),
                },
              });
              mobileOptionsVisibilityHandler.close();
            }}
            variant="tertiary"
          >
            {t('common:filters.actionReset')}
          </LggButton>
          <LggButton
            onClick={() => {
              const { basePeriod, basePeriodRange, comparePeriod, comparePeriodRange } =
                mobileForm.getValues();

              setBaseDateFilter(basePeriod);
              setBaseDateFilterDates(basePeriodRange);
              setComparedToDateFilter(comparePeriod);
              setComparedToDateFilterDates(comparePeriodRange);
              mobileOptionsVisibilityHandler.close();
            }}
          >
            {t('common:save')}
          </LggButton>
        </FooterContainer>
      </BottomDrawer>
    </>
  );
};
